import React, { useState, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CyberpunkThemeProvider } from "@/context/CyberpunkThemeContext";
import { LicenseProvider } from "@/components/LicenseProvider";
import { CryptoProvider } from "@/state/CryptoContext";
import { MainNavigation } from "@/components/MainNavigation";
import { Footer } from "@/components/Footer";
import { CartProvider } from "@/context/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import RegisterRouter from "@/components/RegisterRouter";
import FloatingCartButton from "@/components/FloatingCartButton";
import i18n from './i18n';
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import NotFound from "@/pages/not-found";
import VideosPage from "./pages/Videos";
import BlogPage from "./pages/blog";
import BlogPostPermalinkPage from "./pages/blog/[...slug]";

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<Error | null>(null);
  React.useEffect(() => {
    const handler = (event: ErrorEvent) => {
      setError(event.error || new Error(event.message));
    };
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);
  if (error) {
    return (
      <div style={{ background: '#0C1632', color: '#14F195', padding: 32, borderRadius: 16, margin: 32, fontSize: 18, fontWeight: 700, textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <pre style={{ color: '#fff', fontSize: 14, marginTop: 16 }}>{error.message}</pre>
      </div>
    );
  }
  return <>{children}</>;
}

function App({ lang }: { lang: string }) {
  const [isMobile, setIsMobile] = useState(false);
  const [location] = useLocation();
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  // Detect if we're on a mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  // Defer Google Tag Manager (gtag.js) after app is interactive
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.googletagmanager.com/gtag/js?id=AW-17061710943";
    script.async = true;
    document.body.appendChild(script);

    const inlineScript = document.createElement("script");
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-17061710943');
    `;
    document.body.appendChild(inlineScript);
  }, []);

  const isRegister = location === "/register";

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CryptoProvider>
          <CyberpunkThemeProvider>
            <TooltipProvider>
              <LicenseProvider>
                <CartProvider>
                  <div className="min-h-screen flex flex-col">
                    <ScrollToTop />
                    <MainNavigation />
                    <CartDrawer />
                    <Toaster />
                    <div className="flex-grow">
                      <Switch>
                        <Route path="/" component={Home} />
                        <Route path="/videos" component={VideosPage} />
                        <Route path="/blog/:year/:month/:slug.html" component={BlogPostPermalinkPage} />
                        <Route path="/blog/:rest*" component={BlogPostPermalinkPage} />
                        <Route path="/blog" component={BlogPage} />
                        <Route path="/register">
                          <div className="w-full flex flex-col items-center justify-center py-8 bg-[#0c0c15]">
                            <RegisterRouter onRegistrationSuccess={() => setRegistrationComplete(true)} />
                          </div>
                        </Route>
                        <Route component={NotFound} />
                      </Switch>
                    </div>
                    <Footer />
                    <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white p-2 z-50">
                      Skip to content
                    </a>
                  </div>
                </CartProvider>
              </LicenseProvider>
            </TooltipProvider>
          </CyberpunkThemeProvider>
        </CryptoProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
